import React, {SVGAttributes} from 'react';

import {Angle, Point} from 'util/geometry/GeometryConstants';
import {polarToCartesian} from 'util/geometry/GeometryUtil';

interface ArcProps {
  circleCenter: Point;
  radius: number;
  startAngle: Angle;
  endAngle: Angle;
  pathProps: SVGAttributes<SVGPathElement>;
}

/**
 * Renders an arc along a circle from startAngle to endAngle
 *
 * These angles are in radians, relative to 0 at the top of the circle.
 */
export default function Arc({circleCenter, radius, startAngle, endAngle, pathProps}: ArcProps): JSX.Element {
  if (startAngle === endAngle) {
    endAngle -= 0.001; // Helps rendering logic when startAngle == endAngle
  }
  const start = polarToCartesian(circleCenter, radius, endAngle);
  const end = polarToCartesian(circleCenter, radius, startAngle);

  const largeArc =
    endAngle > startAngle
      ? endAngle - startAngle >= Math.PI
      : 2 * Math.PI - (startAngle - endAngle) >= Math.PI;
  const largeArcFlag = largeArc ? '1' : '0';
  const arcPath = [
    'M',
    start.x,
    start.y,
    'A',
    radius,
    radius,
    0, // No x-axis rotation
    largeArcFlag,
    0, // Sweep flag to indicate we want to go clockwise
    end.x,
    end.y,
  ].join(' ');
  return <path {...pathProps} d={arcPath} fill="none" />;
}
